import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Statistic,
  Table,
  Spin,
  Breadcrumb,
  Tabs,
  Progress,
  Divider,
  Alert,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  TeamOutlined,
  DesktopOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Modal, Form, Select, InputNumber, DatePicker, Segmented, message, Empty, Input } from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";
import { authenticatedFetch } from "@/libs/api";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

// API helper
const apiCall = async (url, options = {}) => {
  const response = await authenticatedFetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  return response.json();
};

export default function CompanySnapshot() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Settlement Rules state
  const [settlementRules, setSettlementRules] = useState([]);
  const [availableRules, setAvailableRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // VG Revenue Shares (np_view_group_company_share)
  const [vgShares, setVgShares] = useState([]);
  const [vgSharesLoading, setVgSharesLoading] = useState(false);
  const [availableVgs, setAvailableVgs] = useState([]);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [editingShare, setEditingShare] = useState(null);
  const [shareForm] = Form.useForm();
  const [previewSum, setPreviewSum] = useState(null); // { existingSum, remaining } for selected VG

  // Permission — only superadmin / settlement:run holders can edit shares
  const roleType = (typeof localStorage !== "undefined" ? localStorage.getItem("roleType") : "") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  // Per-company snapshot Revenue Shares tab is VIEW-ONLY and Super Admin only.
  // CRUD lives at /settlements/vg-shares (global page).
  const canViewShares = isSuperadmin;
  const canEditShares = false; // No editing from CompanySnapshot anymore
  const [ruleForm] = Form.useForm();

  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      try {
        const [companyRes, summaryRes] = await Promise.all([
          apiCall(`/api/companies/${companyId}`),
          apiCall(`/api/companies/${companyId}/account-summary`),
        ]);

        const companyData = companyRes?.data || {};
        const summaryData = summaryRes?.data || {};

        // Backend returns { totals, capacity, monthlyLedger, revenueSharing,
        // groups, equipment, online, realtime } (see
        // backend/src/controllers/companyController.js getCompanyAccountSummary).
        // Map nested objects to the UI's flat shape.
        const totals = summaryData.totals || {};
        const capacity = summaryData.capacity || {};
        const monthlyLedger = summaryData.monthlyLedger || [];
        const latestMonth = monthlyLedger[0] || {};
        const groups = summaryData.groups || {};
        const equipment = summaryData.equipment || {};
        const online = summaryData.online || {};
        const realtime = summaryData.realtime || null;

        setCompany({
          id: companyData.id || companyId,
          name: companyData.company_name || companyData.name || "",
          type: companyData.company_type || "",
          status: companyData.status || "",
          contactName: companyData.contact_name || "",
          contactEmail: companyData.contact_email || "",
          contactPhone: companyData.contact_phone || "",
          createdAt: companyData.created_at || "",
          // Capacity comes from the company's DEFAULT view group (see
          // companyController.getCompanyAccountSummary). Works for both
          // system-owning companies (ShineHub: systems linked into its
          // default VG) and pure-partner companies (Mitcham council: its
          // default VG is Mitcham Council, 1097 systems).
          totalSystems: Number(capacity.systemCount) || 0,
          totalCustomers: Number(capacity.customerCount) || 0,
          totalKw: Number(capacity.totalKw) || 0,
          totalKwh: Number(capacity.totalKwh) || 0,
          // Ledger-derived revenue. totalRevenue = cumulative credits across
          // all runs; monthlyRevenue = the most recent month's credit sum.
          totalRevenue: Number(totals.totalReceivables) || 0,
          totalPayments: Number(totals.totalPayments) || 0,
          pendingAmount: Number(totals.pendingAmount) || 0,
          monthlyRevenue: Number(latestMonth.receivables) || 0,
          avgSystemRevenue: (capacity.systemCount && totals.totalReceivables)
            ? Number(totals.totalReceivables) / Number(capacity.systemCount)
            : 0,
          monthlyData: monthlyLedger,
          // Backend populates these from default VG + Cassandra realtime.
          groups: Number(groups.count) || 0,
          groupsList: groups.list || [],
          onlineSystems: Number(online.onlineSystems) || 0,
          offlineSystems: Number(online.offlineSystems) || 0,
          onlineRate: Number(online.onlineRate) || 0,
          inverterTypes: equipment.inverterTypes || [],
          batteryTypes: equipment.batteryTypes || [],
          realtimeData: realtime || {},
        });
      } catch (err) {
        console.error("Failed to fetch company:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [companyId]);

  // Fetch settlement rules when tab changes to settlement
  useEffect(() => {
    if (activeTab === "settlement" && companyId) {
      fetchSettlementRules();
      fetchAvailableRules();
    }
  }, [activeTab, companyId]);

  // Fetch VG revenue shares when tab changes to revenue-shares
  useEffect(() => {
    if (activeTab === "revenue-shares" && companyId) {
      fetchVgShares();
      fetchAvailableVgs();
    }
  }, [activeTab, companyId]);

  const fetchVgShares = async () => {
    setVgSharesLoading(true);
    try {
      const data = await apiCall(`/api/vg-shares?company_id=${companyId}&include_inactive=1`);
      if (data.success) setVgShares(data.data || []);
    } catch (error) {
      console.error("Failed to fetch VG shares:", error);
    }
    setVgSharesLoading(false);
  };

  const fetchAvailableVgs = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const data = await apiCall(`/api/groups?userId=${userId}`);
      if (data && data.success && Array.isArray(data.data)) {
        setAvailableVgs(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch view groups:", error);
    }
  };

  const handleAddShare = () => {
    setEditingShare(null);
    setPreviewSum(null);
    shareForm.resetFields();
    shareForm.setFieldsValue({
      effective_from: dayjs(),
      effective_to: null,
      priority: 100,
      is_active: 1,
    });
    setShareModalVisible(true);
  };

  const handleEditShare = (record) => {
    setEditingShare(record);
    setPreviewSum(null);
    shareForm.setFieldsValue({
      view_group_id: record.view_group_id,
      share_percent: Number(record.share_percent),
      description: record.description,
      effective_from: record.effective_from ? dayjs(record.effective_from) : null,
      effective_to: record.effective_to ? dayjs(record.effective_to) : null,
      priority: record.priority,
      is_active: record.is_active,
    });
    setShareModalVisible(true);
  };

  // Probe current sum when VG / effective_from changes — gives user "remaining %" hint
  const refreshPreviewSum = async (viewGroupId, effectiveFrom) => {
    if (!viewGroupId || !effectiveFrom) { setPreviewSum(null); return; }
    try {
      const fromStr = dayjs.isDayjs(effectiveFrom) ? effectiveFrom.format("YYYY-MM-DD") : effectiveFrom;
      const data = await apiCall(`/api/vg-shares/preview-sum?view_group_id=${viewGroupId}&effective_from=${fromStr}`);
      if (data.success) {
        // If editing this share, subtract its own current % from existingSum so the
        // remaining hint reflects what's available to THIS row when re-set.
        let existing = Number(data.data.existingSum);
        if (editingShare && editingShare.view_group_id === viewGroupId && Number(editingShare.is_active) === 1) {
          existing = existing - Number(editingShare.share_percent);
        }
        setPreviewSum({ existingSum: existing, remaining: 100 - existing });
      }
    } catch (e) {
      setPreviewSum(null);
    }
  };

  const handleSaveShare = async (values) => {
    const body = {
      view_group_id: values.view_group_id,
      company_id: companyId,
      share_percent: Number(values.share_percent),
      description: values.description || null,
      effective_from: dayjs(values.effective_from).format("YYYY-MM-DD"),
      effective_to: values.effective_to ? dayjs(values.effective_to).format("YYYY-MM-DD") : null,
      priority: values.priority || 100,
      is_active: values.is_active != null ? Number(values.is_active) : 1,
    };
    try {
      const url = editingShare ? `/api/vg-shares/${editingShare.id}` : `/api/vg-shares`;
      const method = editingShare ? "PUT" : "POST";
      const data = await apiCall(url, { method, body: JSON.stringify(body) });
      if (data.success) {
        message.success(editingShare ? "Share updated" : "Share created");
        setShareModalVisible(false);
        fetchVgShares();
      } else {
        message.error(data.message || "Save failed");
      }
    } catch (error) {
      message.error("Save failed: " + error.message);
    }
  };

  const handleDeleteShare = (record) => {
    Modal.confirm({
      title: "Delete this revenue share?",
      content: `${record.view_group_name || record.view_group_id}: ${record.share_percent}%`,
      okType: "danger",
      onOk: async () => {
        try {
          const data = await apiCall(`/api/vg-shares/${record.id}`, { method: "DELETE" });
          if (data.success) {
            message.success("Deleted");
            fetchVgShares();
          } else {
            message.error(data.message || "Delete failed");
          }
        } catch (error) {
          message.error("Delete failed: " + error.message);
        }
      },
    });
  };

  const fetchSettlementRules = async () => {
    setRulesLoading(true);
    try {
      const data = await apiCall(`/api/settlements/bindings/company?entityId=${companyId}`);
      if (data.success) {
        setSettlementRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch settlement rules:", error);
    }
    setRulesLoading(false);
  };

  const fetchAvailableRules = async () => {
    try {
      const data = await apiCall("/api/settlements/rules?ruleType=COMPANY_REVENUE");
      if (data.success) {
        setAvailableRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch available rules:", error);
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    ruleForm.resetFields();
    ruleForm.setFieldsValue({
      effectiveFrom: dayjs(),
      priority: 100,
      isActive: true,
    });
    setRuleModalVisible(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    ruleForm.setFieldsValue({
      ruleId: rule.rule_id,
      paramsOverride: rule.paramsOverride,
      effectiveFrom: rule.effective_from ? dayjs(rule.effective_from) : null,
      effectiveTo: rule.effective_to ? dayjs(rule.effective_to) : null,
      priority: rule.priority,
      isActive: rule.is_active,
    });
    setRuleModalVisible(true);
  };

  const handleDeleteRule = async (bindingId) => {
    try {
      const data = await apiCall(`/api/settlements/bindings/company/${bindingId}`, {
        method: "DELETE",
      });
      if (data.success) {
        message.success("Rule deleted successfully");
        fetchSettlementRules();
      } else {
        message.error(data.message || "Failed to delete rule");
      }
    } catch (error) {
      message.error("Failed to delete rule");
    }
  };

  const handleSaveRule = async () => {
    try {
      const values = await ruleForm.validateFields();
      const payload = {
        entityId: companyId,
        ruleId: values.ruleId,
        paramsOverride: values.paramsOverride || null,
        effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD"),
        effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
        priority: values.priority || 100,
        isActive: values.isActive !== false,
      };

      let data;
      if (editingRule) {
        data = await apiCall(`/api/settlements/bindings/company/${editingRule.binding_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        data = await apiCall("/api/settlements/bindings/company", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (data.success) {
        message.success(editingRule ? "Rule updated successfully" : "Rule added successfully");
        setRuleModalVisible(false);
        fetchSettlementRules();
      } else {
        message.error(data.message || "Failed to save rule");
      }
    } catch (error) {
      console.error("Save rule error:", error);
    }
  };

  if (loading) {
    return (
      <PageLayout active="companySnapshot">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!company) {
    return (
      <PageLayout active="companySnapshot">
        <div style={{ textAlign: "center", padding: 48 }}>
          <Text type="secondary">Company not found</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate("/company/list")}>
              Back to Company List
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Group breakdown columns. Some per-group metrics (systems/kw/kwh/online/
  // offline/revenue) aren't returned by the backend yet — render with
  // null-safe defaults so the page doesn't crash on undefined.toFixed().
  const groupColumns = [
    {
      title: "Group",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Button type="link" onClick={() => navigate(`/group-management/groups/${record.id}`)} style={{ padding: 0 }}>
          {name}
        </Button>
      ),
    },
    {
      title: "Systems",
      dataIndex: "systems",
      key: "systems",
      align: "center",
      render: (v) => v ?? "—",
    },
    {
      title: "Inverter (kW)",
      dataIndex: "kw",
      key: "kw",
      align: "right",
      render: (v) => v ?? "—",
    },
    {
      title: "Battery (kWh)",
      dataIndex: "kwh",
      key: "kwh",
      align: "right",
      render: (v) => v ?? "—",
    },
    {
      title: "Online",
      key: "online",
      align: "center",
      render: (_, record) =>
        record.online == null && record.offline == null
          ? <Text type="secondary">—</Text>
          : (
            <Space>
              <Tag color="success">{record.online ?? 0}</Tag>
              <Tag color="error">{record.offline ?? 0}</Tag>
            </Space>
          ),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      align: "right",
      render: (v) => v == null
        ? <Text type="secondary">—</Text>
        : <Text style={{ color: token.colorSuccess }}>${Number(v).toFixed(2)}</Text>,
    },
  ];

  const tabItems = [
    {
      key: "overview",
      label: "Overview",
      children: (
        <div>
          {/* Summary Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Groups" value={company.groups} prefix={<TeamOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Customers" value={company.totalCustomers} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic title="Systems" value={company.totalSystems} prefix={<DesktopOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                {(() => { const f = formatPower(company.totalKw || 0, "kW"); return (
                <Statistic title="Inverter" value={f.value} suffix={f.suffix} valueStyle={{ color: "#faad14" }} />); })()}
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                {(() => { const f = formatPower(company.totalKwh || 0, "kWh"); return (
                <Statistic title="Battery" value={f.value} suffix={f.suffix} valueStyle={{ color: "#f5a623" }} />); })()}
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Online Rate"
                  value={company.onlineRate}
                  suffix="%"
                  valueStyle={{ color: company.onlineRate >= 95 ? token.colorSuccess : token.colorWarning }}
                />
              </Card>
            </Col>
          </Row>

          {/* Online/Offline Status */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: 12 }} title="System Status">
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ textAlign: "center" }}>
                      <Progress
                        type="circle"
                        percent={company.onlineRate}
                        strokeColor={token.colorSuccess}
                        format={() => (
                          <div>
                            <CheckCircleOutlined style={{ fontSize: 24, color: token.colorSuccess }} />
                            <div style={{ fontSize: 20, fontWeight: 600 }}>{company.onlineSystems}</div>
                            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>Online</div>
                          </div>
                        )}
                        size={120}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ textAlign: "center" }}>
                      <Progress
                        type="circle"
                        percent={(company.offlineSystems / company.totalSystems) * 100}
                        strokeColor={token.colorError}
                        format={() => (
                          <div>
                            <CloseCircleOutlined style={{ fontSize: 24, color: token.colorError }} />
                            <div style={{ fontSize: 20, fontWeight: 600 }}>{company.offlineSystems}</div>
                            <div style={{ fontSize: 12, color: token.colorTextSecondary }}>Offline</div>
                          </div>
                        )}
                        size={120}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} style={{ borderRadius: 12 }} title="Realtime Status">
                <Row gutter={16}>
                  <Col span={8}>
                    {(() => { const f = formatPower(company.realtimeData.totalPower || 0, "kW"); return (
                    <Statistic
                      title="Total Power"
                      value={f.value}
                      suffix={f.suffix}
                      valueStyle={{ color: token.colorSuccess }}
                    />); })()}
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Avg SOC"
                      value={company.realtimeData.avgSOC}
                      suffix="%"
                      valueStyle={{ color: "#f5a623" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Dispatching"
                      value={company.realtimeData.dispatchingNow}
                      valueStyle={{ color: token.colorPrimary }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: "16px 0" }} />
                <Text type="secondary">Last updated: {company.realtimeData.lastUpdate}</Text>
              </Card>
            </Col>
          </Row>

          {/* Revenue Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Total Revenue"
                  value={company.totalRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: token.colorSuccess, fontSize: 28 }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="This Month"
                  value={company.monthlyRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: token.colorPrimary, fontSize: 28 }}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={{ borderRadius: 12 }}>
                <Statistic
                  title="Avg per System"
                  value={company.avgSystemRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ fontSize: 28 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Groups Table */}
          <Card bordered={false} style={{ borderRadius: 12 }} title="Groups Breakdown">
            <Table
              columns={groupColumns}
              dataSource={company.groupsList}
              rowKey="id"
              pagination={false}
              summary={(data) => {
                const totals = data.reduce(
                  (acc, row) => ({
                    systems: acc.systems + (Number(row.systems) || 0),
                    kw: acc.kw + (Number(row.kw) || 0),
                    kwh: acc.kwh + (Number(row.kwh) || 0),
                    online: acc.online + (Number(row.online) || 0),
                    offline: acc.offline + (Number(row.offline) || 0),
                    revenue: acc.revenue + (Number(row.revenue) || 0),
                  }),
                  { systems: 0, kw: 0, kwh: 0, online: 0, offline: 0, revenue: 0 }
                );
                return (
                  <Table.Summary.Row style={{ background: token.colorFillQuaternary }}>
                    <Table.Summary.Cell><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="center"><Text strong>{totals.systems}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><Text strong>{totals.kw}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="right"><Text strong>{totals.kwh}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell align="center">
                      <Space>
                        <Tag color="success">{totals.online}</Tag>
                        <Tag color="error">{totals.offline}</Tag>
                      </Space>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong style={{ color: token.colorSuccess }}>${totals.revenue.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </div>
      ),
    },
    {
      key: "historical",
      label: "Historical Data",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Revenue">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={token.colorSuccess} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={token.colorSuccess} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                    <RechartsTooltip formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" stroke={token.colorSuccess} fill="url(#revenueGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Dispatches">
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip />
                    <Bar dataKey="dispatches" name="Dispatches" fill={token.colorPrimary} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Monthly Energy Discharged">
              <div style={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={company.monthlyData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => [`${v} kWh`, "Energy"]} />
                    <Bar dataKey="energy" name="Energy (kWh)" fill="#faad14" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: "equipment",
      label: "Equipment",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Inverter Distribution">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.inverterTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {company.inverterTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Divider />
              <Space wrap>
                {company.inverterTypes.map((item) => (
                  <Tag key={item.name} color={item.color}>
                    {item.name}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card bordered={false} style={{ borderRadius: 12 }} title="Battery Distribution">
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.batteryTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {company.batteryTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Divider />
              <Space wrap>
                {company.batteryTypes.map((item) => (
                  <Tag key={item.name} color={item.color}>
                    {item.name}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: "settlement",
      label: (
        <span>
          <SettingOutlined /> Settlement Rules
        </span>
      ),
      children: (
        <div>
          {/*
            Settlement-rule management used to live here, talking to
            /api/settlements/bindings/company with entityId=companyId.
            After the 2026-05 refactor the partner-share model is per-row
            on np_view_group_company_share, so the binding entity is
            actually vg_share.id — not company.id. The old API contract is
            gone and the page would silently fail. We keep the tab as a
            redirect so existing bookmarks still land somewhere useful.
          */}
          <Alert
            type="info"
            showIcon
            message="Settlement rule configuration has moved"
            description={
              <div>
                <p style={{ marginTop: 4 }}>
                  Partner revenue shares (which VGs / partner companies / share %) and
                  the algorithm bound to each share row are now managed from two
                  dedicated screens:
                </p>
                <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                  <li>
                    <Text strong>Companies → Partner Revenue Shares</Text> tab —
                    add/edit (VG, partner, share %) rows.
                  </li>
                  <li>
                    <Text strong>Settlement Rules → Rule Bindings → Partner Share</Text> tab —
                    attach an algorithm (settlement rule) to a specific share row.
                  </li>
                </ul>
                <Space>
                  <Button type="primary" onClick={() => navigate("/company")}>
                    Open Partner Revenue Shares
                  </Button>
                  <Button onClick={() => navigate("/settlements/rules")}>
                    Open Rule Bindings
                  </Button>
                </Space>
              </div>
            }
          />

        </div>
      ),
    },
    // Revenue Shares tab — Super Admin only, read-only. CRUD lives at
    // /settlements/vg-shares (Partner Revenue Shares management page).
    ...(canViewShares ? [{
      key: "revenue-shares",
      label: (
        <span>
          <DollarOutlined /> Revenue Shares
        </span>
      ),
      children: (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Read-only view"
            description={
              <span>
                This is a read-only snapshot of revenue shares paid to <Text strong>{company.name}</Text>.
                To add / edit / delete shares, go to{" "}
                <a href="/settlements/vg-shares">Settlements → Partner Revenue Shares</a>
                {" "}(Super Admin only).
              </span>
            }
          />
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              View groups paying revenue share to <Text strong>{company.name}</Text>.
              Each row = one share allocation (e.g. own council %, founding council %).
            </Text>
          </div>

          {vgSharesLoading ? (
            <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>
          ) : vgShares.length === 0 ? (
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No revenue shares configured for this company"
              />
            </Card>
          ) : (
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Table
                dataSource={vgShares}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: "View Group",
                    key: "vg",
                    render: (_, r) => (
                      <div>
                        <Text strong>{r.view_group_name || r.view_group_id}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>{r.view_group_id}</Text>
                      </div>
                    ),
                  },
                  {
                    title: "Share %",
                    key: "share",
                    align: "right",
                    width: 100,
                    render: (_, r) => (
                      <Text strong style={{ color: token.colorPrimary }}>
                        {Number(r.share_percent).toFixed(2)}%
                      </Text>
                    ),
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                    render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>,
                  },
                  {
                    title: "Effective Period",
                    key: "period",
                    render: (_, r) => (
                      <span>
                        {dayjs(r.effective_from).format("YYYY-MM-DD")}
                        {" → "}
                        {r.effective_to ? dayjs(r.effective_to).format("YYYY-MM-DD") : "Indefinite"}
                      </span>
                    ),
                  },
                  {
                    title: "Status",
                    key: "status",
                    width: 100,
                    align: "center",
                    render: (_, r) => Number(r.is_active) === 1
                      ? <Tag color="success">Active</Tag>
                      : <Tag>Inactive</Tag>,
                  },
                  ...(canEditShares ? [{
                    title: "Actions",
                    key: "actions",
                    width: 110,
                    align: "center",
                    render: (_, r) => (
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEditShare(r)} />
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteShare(r)} />
                      </Space>
                    ),
                  }] : []),
                ]}
              />
            </Card>
          )}

          {/* Add / Edit Share Modal */}
          <Modal
            title={editingShare ? "Edit Revenue Share" : "Add Revenue Share"}
            open={shareModalVisible}
            onCancel={() => setShareModalVisible(false)}
            onOk={() => shareForm.submit()}
            okText={editingShare ? "Save" : "Create"}
            destroyOnClose
            width={640}
          >
            <Form
              form={shareForm}
              layout="vertical"
              onFinish={handleSaveShare}
            >
              <Form.Item
                name="view_group_id"
                label="View Group"
                rules={[{ required: true, message: "Pick a view group" }]}
              >
                <Select
                  showSearch
                  placeholder="Select view group"
                  disabled={!!editingShare}
                  optionFilterProp="label"
                  options={availableVgs.map((vg) => ({
                    value: vg.id,
                    label: `${vg.name} (${vg.id})`,
                  }))}
                  onChange={(vgId) => {
                    const ef = shareForm.getFieldValue("effective_from");
                    refreshPreviewSum(vgId, ef);
                  }}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="share_percent"
                    label="Share %"
                    rules={[
                      { required: true, message: "Share % required" },
                      { type: "number", min: 0, max: 100, message: "0-100" },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      step={0.01}
                      precision={2}
                      placeholder="e.g. 12.00"
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="priority" label="Priority (lower = higher precedence)">
                    <InputNumber style={{ width: "100%" }} min={0} placeholder="100" />
                  </Form.Item>
                </Col>
              </Row>

              {previewSum && (
                <div style={{ marginBottom: 16, padding: "8px 12px", background: token.colorFillTertiary, borderRadius: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Current allocations in this VG (overlapping window):
                    {" "}<Text strong>{previewSum.existingSum.toFixed(2)}%</Text>
                    {" "}—{" "}
                    Remaining: <Text strong style={{ color: previewSum.remaining > 0 ? token.colorSuccess : token.colorError }}>
                      {previewSum.remaining.toFixed(2)}%
                    </Text>
                  </Text>
                </div>
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="effective_from"
                    label="Effective From"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      onChange={(date) => {
                        const vgId = shareForm.getFieldValue("view_group_id");
                        refreshPreviewSum(vgId, date);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="effective_to" label="Effective To (leave empty for open-ended)">
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label="Description (optional — e.g. contract clause reference)"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="e.g. clause 2.2c founding council share"
                />
              </Form.Item>

              <Form.Item name="is_active" label="Active">
                <Segmented
                  options={[
                    { label: "Active", value: 1 },
                    { label: "Inactive (paused)", value: 0 },
                  ]}
                />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      ),
    }] : []),
  ];

  return (
    <PageLayout active="companySnapshot">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate("/company/list")}>Companies</a> },
          { title: company.name },
        ]}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/company/list")}>
            Back
          </Button>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <Title level={2} style={{ marginBottom: 0 }}>{company.name}</Title>
              <Tag color={company.type === "Aggregator" ? "blue" : "green"}>{company.type}</Tag>
              <Tag color="success">{company.status}</Tag>
            </div>
            <Text type="secondary">
              {company.contactName} | {company.contactEmail} | {company.contactPhone}
            </Text>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </PageLayout>
  );
}
